// ---- CartForm -----------------------------------------------------------

if ( ! customElements.get('cart-form') ) {
	class CartForm extends HTMLElement {

		constructor(){
			super();
			this._pending = 0;
			// Lines with a removal queued or in flight.
			this._removing = new Set();
			// Enter in a quantity field must not submit the form: its default button is Checkout.
			this.addEventListener('keydown', e => {
				if ( e.key === 'Enter' && e.target.classList.contains('qty') ) {
					e.preventDefault();
				}
			});
			this.ajaxifyCartItems();
		}

		ajaxifyCartItems(){

			this.form = this.querySelector('form');

			this.querySelectorAll('[data-js-cart-item]').forEach(item=>{

				const remove = item.querySelector('.remove');
				if ( remove ) {
					remove.dataset.href = remove.getAttribute('href');
					remove.setAttribute('href', '');
					remove.addEventListener('click', (e)=>{
						e.preventDefault();
						this.updateCartQty(item, 0);
					})
				}

				const qty = item.querySelector('.qty');
				if ( qty ) {
					qty.addEventListener('input', debounce(e=>{
						e.preventDefault();
						e.target.blur();
						this.updateCartQty(item, parseInt(qty.value));
					}, 500));
					qty.addEventListener('click', (e)=>{
						e.target.select();
					})
				}

			})

		}

		// Each change names its line by key and waits its turn in the shared cart queue, so it lands on the line it was made on.
		updateCartQty(item, newQty){

			const key = item.dataset.id;
			if ( ! Number.isInteger(newQty) || newQty < 0 ) {
				this.revertQty(key);
				return;
			}
			// A second Remove (Enter pressed twice) while the first is queued would change a line that's already gone.
			if ( newQty === 0 ) {
				if ( this._removing.has(key) ) return;
				this._removing.add(key);
			}

			this._pending++;
			this.syncProcessing();
			if ( this.querySelector('.alert') ) {
				this.querySelector('.alert').remove();
			}

			OrdoUtils.cart.request(`${KROWN.settings.routes.cart_change_url}.js`, { id: key, quantity: newQty })
				.then(({ status, message, html }) => {
					// A newer change is queued: keep this answer and let the last one redraw, so fields aren't reset part-way.
					if ( this._pending > 1 && ! message && status < 400 ) {
						this._latestHtml = html;
						this._latestAt = OrdoUtils.cart.renders();
						return;
					}
					this._latestHtml = null;
					if ( ! OrdoUtils.cart.render(html) ) {
						// No cart to redraw: a refused change goes back with its error; a done one fetches the cart once more,
						// and says so when that fails too.
						if ( message || status >= 400 ) {
							this.revertQty(key);
							OrdoUtils.cart.error(message || OrdoUtils.localeText('cart_general_error'));
						} else if ( window.refreshCart ) {
							window.refreshCart({ open: false }).catch(() => OrdoUtils.cart.error(OrdoUtils.localeText('cart_general_error')));
						}
						return;
					}
					if ( message ) {
						OrdoUtils.cart.error(message);
					} else if ( status >= 400 ) {
						OrdoUtils.cart.error(OrdoUtils.localeText('cart_general_error'));
					} else {
						OrdoUtils.cart.announce();
					}
				})
				.catch(e => {
					console.log(e);
					// Show the cart as the last answered change left it, unless a newer cart was drawn since (an add or an app);
					// with neither, the field goes back to the drawn quantity.
					const held = this._latestHtml && OrdoUtils.cart.renders() === this._latestAt ? this._latestHtml : null;
					if ( ! ( held && OrdoUtils.cart.render(held) ) ) this.revertQty(key);
					this._latestHtml = null;
					OrdoUtils.cart.error(OrdoUtils.localeText('cart_general_error'));
				})
				.finally(() => {
					if ( newQty === 0 ) this._removing.delete(key);
					this._pending--;
					this.syncProcessing();
				});
		}

		// Back to the quantity the cart last rendered, with the −/+ states to match.
		revertQty(key){
			const item = [...this.querySelectorAll('[data-js-cart-item]')].find(i => i.dataset.id === key);
			const qty = item?.querySelector('.qty');
			if ( ! qty ) return;
			qty.value = item.dataset.qty;
			const min = qty.hasAttribute('min') ? parseInt(qty.getAttribute('min')) : 1;
			const max = qty.hasAttribute('max') ? parseInt(qty.getAttribute('max')) : 999;
			item.querySelector('.qty-minus')?.classList.toggle('disabled', parseInt(qty.value) <= min);
			item.querySelector('.qty-plus')?.classList.toggle('disabled', parseInt(qty.value) >= max);
		}

		// Dimmed while any change is queued or in flight; every render brings a new form.
		syncProcessing(){
			this.querySelector('form')?.classList.toggle('processing', this._pending > 0);
		}

	}


  if ( typeof customElements.get('cart-form') == 'undefined' ) {
		customElements.define('cart-form', CartForm);
	}

}

// ---- CartProductQuantity ------------------------------------------------

if ( ! customElements.get('cart-product-quantity') ) {

	class CartProductQuantity extends HTMLElement {
		constructor(){
			super();
			// Per-instance debounce coalesces rapid +/- clicks into a single AJAX call
			// using the latest qty value (set optimistically by the outer <product-quantity>).
			this._debouncedUpdate = debounce(()=>{
				const ajaxForm = document.getElementById('AjaxCartForm');
				const item = this.closest('[data-js-cart-item]');
				const input = this.querySelector('.qty');
				if ( ajaxForm && item && input ) {
					ajaxForm.updateCartQty(item, parseInt(input.value));
				}
			}, 300);
			this.querySelector('.qty-minus').addEventListener('click', this.changeCartInput.bind(this));
			this.querySelector('.qty-plus').addEventListener('click', this.changeCartInput.bind(this));
		}
		changeCartInput(){
			this._debouncedUpdate();
		}
	}

  if ( typeof customElements.get('cart-product-quantity') == 'undefined' ) {
		customElements.define('cart-product-quantity', CartProductQuantity);
	}

}

// ---- window.refreshCart (public hook for apps) --------------------------

if ( ! window.refreshCart ) {

	// open: false re-renders without opening the drawer (Shopify's updateCart action uses that).
	window.refreshCart = (options) => {

		const open = ! options || options.open !== false;

		return OrdoUtils.cart.refresh()
			.then(text => {

				if ( ! OrdoUtils.cart.render(text) ) return;

				if ( open && document.querySelector('[data-js-site-cart-sidebar]') ) {
					document.querySelector('[data-js-site-cart-sidebar]').show();
				}

				OrdoUtils.cart.announce();

			})

	}

}
